import { type ExternalIntegrationProvider, ExternalIntegrationStatus } from "@bbj-types/externalIntegrations";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ReplayOutlined from "@mui/icons-material/ReplayOutlined";
import { Button, ButtonBase, Link, Popover, Stack, Typography, type TypographyProps } from "@mui/material";
import {
  type ExternalIntegrationErrorLinkAction,
  type IExternalIntegrationErrorMessage,
  getExternalIntegrationErrorMessage,
} from "@root/utils/externalIntegrations/errorMessages";
import { EXTERNAL_INTEGRATION_PROVIDERS_LABELS } from "@root/utils/externalIntegrations/labels";
import { type MouseEvent, useState } from "react";

const DEFAULT_PROPS = {
  variant: "body2",
  sx: { lineHeight: 1.2 },
} satisfies Partial<TypographyProps>;

export interface IExternalIntegrationStatusLabelProps extends Partial<TypographyProps> {
  provider: ExternalIntegrationProvider;
  status: ExternalIntegrationStatus;
  error?: string;
  onErrorLinkClick?: (action: ExternalIntegrationErrorLinkAction) => void;
  onRetry?: () => Promise<unknown> | undefined;
}

const getColor = (status: ExternalIntegrationStatus) => {
  switch (status) {
    case ExternalIntegrationStatus.SUCCESS:
      return "success";
    case ExternalIntegrationStatus.PENDING:
      return "paused";
    case ExternalIntegrationStatus.ERROR:
      return "secondary";
  }
};

const getText = (status: ExternalIntegrationStatus, provider: ExternalIntegrationProvider) => {
  switch (status) {
    case ExternalIntegrationStatus.SUCCESS:
      return `Transmis à ${EXTERNAL_INTEGRATION_PROVIDERS_LABELS[provider]}`;
    case ExternalIntegrationStatus.PENDING:
      return `Attente de transmission à ${EXTERNAL_INTEGRATION_PROVIDERS_LABELS[provider]}`;
    case ExternalIntegrationStatus.ERROR:
      return `Erreur de transmission à ${EXTERNAL_INTEGRATION_PROVIDERS_LABELS[provider]}`;
  }
};

interface IErrorSolutionProps {
  errorMessage: IExternalIntegrationErrorMessage;
  onLinkClick?: (action: ExternalIntegrationErrorLinkAction) => void;
}

const ErrorSolution = ({ errorMessage: { solution, link }, onLinkClick }: IErrorSolutionProps) => {
  if (!solution) {
    return null;
  }
  const linkIndex = link && onLinkClick ? solution.indexOf(link.label) : -1;
  if (!link || !onLinkClick || linkIndex === -1) {
    return <Typography>{solution}</Typography>;
  }
  return (
    <Typography>
      {solution.slice(0, linkIndex)}
      <Link
        component="button"
        onClick={() => onLinkClick(link.action)}
        sx={{ fontWeight: 600, verticalAlign: "baseline", font: "inherit" }}
      >
        {link.label}
      </Link>
      {solution.slice(linkIndex + link.label.length)}
    </Typography>
  );
};

export const ExternalIntegrationStatusLabel = ({
  provider,
  status,
  error,
  onErrorLinkClick,
  onRetry,
  ...typographyProps
}: IExternalIntegrationStatusLabelProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [retrying, setRetrying] = useState(false);

  if (status !== ExternalIntegrationStatus.ERROR) {
    return (
      <Typography {...DEFAULT_PROPS} {...typographyProps} noWrap sx={{ height: "100%" }} color={getColor(status)}>
        {getText(status, provider)}
      </Typography>
    );
  }

  const errorMessage: IExternalIntegrationErrorMessage = getExternalIntegrationErrorMessage(provider, error) ?? {
    message: `L'erreur renvoyée par ${EXTERNAL_INTEGRATION_PROVIDERS_LABELS[provider]} n'a pas pu être identifiée, veuillez contacter le support.`,
  };

  const handleOpen = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => setAnchorEl(null);
  const handleLinkClick = (action: ExternalIntegrationErrorLinkAction) => {
    handleClose();
    onErrorLinkClick?.(action);
  };
  const handleRetry = async () => {
    setRetrying(true);
    const succeeded = await Promise.resolve(onRetry?.()).then(
      () => true,
      () => false
    );
    setRetrying(false);
    if (succeeded) {
      handleClose();
    }
  };

  return (
    <>
      <ButtonBase
        disableRipple
        onClick={handleOpen}
        aria-haspopup="dialog"
        aria-expanded={Boolean(anchorEl)}
        sx={{ gap: 0.5, justifyContent: "flex-start", alignSelf: "flex-start", textAlign: "left" }}
      >
        <Typography
          {...DEFAULT_PROPS}
          {...typographyProps}
          noWrap
          sx={{ height: "100%", textDecoration: "underline" }}
          color={getColor(status)}
        >
          {getText(status, provider)}
        </Typography>
        <InfoOutlinedIcon sx={{ fontSize: 16, color: "secondary.main" }} />
      </ButtonBase>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        onClick={(event) => event.stopPropagation()}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: -8, horizontal: "left" }}
        slotProps={{ paper: { sx: { maxWidth: 400, p: 2 } } }}
      >
        <Stack sx={{ gap: 2, alignItems: "flex-start" }}>
          <Stack sx={{ gap: 1 }}>
            <Typography>{errorMessage.message}</Typography>
            <ErrorSolution errorMessage={errorMessage} onLinkClick={onErrorLinkClick && handleLinkClick} />
          </Stack>
          {errorMessage.retry && onRetry && (
            <Button
              variant="outlined"
              size="small"
              loading={retrying}
              startIcon={<ReplayOutlined />}
              onClick={handleRetry}
            >
              Réessayer
            </Button>
          )}
        </Stack>
      </Popover>
    </>
  );
};
